import React, { useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { ChevronRight } from 'react-feather';
import { useHistory } from 'react-router-dom';

import { ACTION_CODES } from 'constants/errors';
import AgentPortalError from 'errors/AgentPortalError';
import { Column, Row } from 'components/shared/Layout';
import FormattedCurrency from 'components/ui/FormattedCurrency';
import { GET_HOTEL_BOOKING_INFO, PREVIEW_IMPORT_HOTEL_BOOKING } from 'store/queries';
import { sum } from 'utils/calculator';
import { jacTravelReferenceRegex, hotelIdRegex } from 'utils/validators';
import { marginTariff } from 'constants/tariffCodes';
import { ConfirmButton } from 'components/ui/Button';
import {
  ADD_ADDITIONAL_DETAILS,
  ADD_EXTERNAL_REFERENCE,
  CANCEL_BOOKING,
  CREATE_PROVISIONAL_HOTEL_ONLY,
  OVERRIDE_PRICE,
  IMPORT_HOTEL_BOOKING
} from 'store/mutations';
import {
  HotelOnlyContainer,
  HotelDetailsContainer,
  StyledErrorMessage,
  StyledHeader,
  StyledHr,
  StyledFormContainer,
  StyledInputWrapper,
  StyledLabel,
  StyledPrice,
  SubmitButton,
  VerticalLine
} from './HotelOnly.styles';
import { buildCreateProvisionalBookingVariables, getErrorKey, buildAdditionalDetails } from './HotelOnly.utils';
import { buildHotelInfo, buildHotelInfoFromDotwBooking } from 'utils/hotel';

const JAC_REFERENCE = 'JAC';
const EHL_REFERENCE = 'EHL';

const HotelOnly = () => {
  const history = useHistory();

  const [hotelInfo, setHotelInfo] = useState(null);
  const [isCreatingBooking, setIsCreatingBooking] = useState(false);

  const { register, watch, setValue, unregister, formState } = useForm({
    reValidateMode: 'onChange',
    mode: 'onChange'
  });

  const values = watch();
  const { hotelId, jacTravelRef, eurostarPackageRef } = values;

  const hasErrors = Boolean(Object.keys(formState.errors).length > 0);

  const [getHotelBookingInfo, getHotelBookingInfoQuery] = useLazyQuery(GET_HOTEL_BOOKING_INFO, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (data?.getHotelBooking) {
        setHotelInfo(buildHotelInfo(data?.getHotelBooking));
      }
    }
  });

  const [previewImportHotelBooking, previewImportHotelBookingQuery] = useLazyQuery(PREVIEW_IMPORT_HOTEL_BOOKING, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (data?.previewImportHotelBooking) {
        setHotelInfo(buildHotelInfoFromDotwBooking(data?.previewImportHotelBooking));
        unregister(['hotelId', 'eurostarPackageRef']);
        setValue('hotelId', data?.previewImportHotelBooking?.hotel?.id, { shouldValidate: true });
      }
    }
  });

  const [overridePrice] = useMutation(OVERRIDE_PRICE);
  const [cancelBooking, { error: cancelBookingError }] = useMutation(CANCEL_BOOKING);
  const [importHotelBooking] = useMutation(IMPORT_HOTEL_BOOKING);
  const [addExternalReference] = useMutation(ADD_EXTERNAL_REFERENCE);
  const [addAdditionalDetails] = useMutation(ADD_ADDITIONAL_DETAILS);
  const [createProvisionalBooking, { error: createProvisionalBookingError }] = useMutation(CREATE_PROVISIONAL_HOTEL_ONLY, {
    onError: (error) => {
      setIsCreatingBooking(false);
    }
  });

  const isHotelBeenFound = hotelInfo && jacTravelRef === hotelInfo?.bookingReference;

  const isGetHotelInfoCalled = getHotelBookingInfoQuery.called || previewImportHotelBookingQuery.called;
  const isGetHotelInfoLoading = getHotelBookingInfoQuery.loading || previewImportHotelBookingQuery.loading;
  const error = cancelBookingError || createProvisionalBookingError;

  const errorKey = getErrorKey({
    hotelInfo,
    hasErrors,
    errors: formState.errors,
    hotelLoading: isGetHotelInfoLoading,
    getHotelCalled: isGetHotelInfoCalled
  });

  const isHotelFormSubmitDisabled = !jacTravelRef || formState.errors.jacTravelRef || formState.isSubmitting;
  const isCreateBookingButtonEnabled = isHotelBeenFound && !isCreatingBooking && !errorKey;

  const onSubmitJacTravelRef = async () => {
    setHotelInfo(null);
    await Promise.all([
      previewImportHotelBooking({
        variables: {
          options: {
            bookingCode: jacTravelRef,
            provider: 'JACTRAVEL',
            product: 'HOTELS',
            language: 'EN',
            market: 'UK'
          }
        }
      }),
      getHotelBookingInfo({
        variables: {
          externalReference: jacTravelRef,
          productType: 'HOTELS'
        }
      })
    ]);
  };

  const createHotelBooking = async () => {
    setIsCreatingBooking(true);

    const provisionalBooking = await createProvisionalBooking({
      variables: buildCreateProvisionalBookingVariables({ hotelInfo, marginTariff })
    });

    if (!provisionalBooking?.data?.createBooking) return;

    const { reference, hotels, outboundExtras } = provisionalBooking?.data?.createBooking ?? {};
    const marginItemRef = Number(outboundExtras?.[0]?.itemRef);
    const hotelItemRef = Number(hotels?.[0]?.itemRef);

    try {
      await overridePrice({
        variables: {
          reference,
          items: [
            { itemRefId: hotelItemRef, price: hotelInfo?.isDotW ? hotelInfo.priceBeforeMargin : hotelInfo.price },
            { itemRefId: marginItemRef, price: hotelInfo.margin }
          ]
        }
      });

      await addExternalReference({
        variables: {
          reference,
          externalReference: { id: jacTravelRef, source: JAC_REFERENCE }
        }
      });

      await addAdditionalDetails({
        variables: {
          bookingReference: reference,
          additionalDetails: buildAdditionalDetails({
            eurostarPackageRef: hotelInfo?.isDotW ? reference : eurostarPackageRef,
            hotelItemRef,
            hotelInfo,
            hotelId
          })
        }
      });

      if (hotelInfo?.isDotW) {
        await addExternalReference({
          variables: {
            reference,
            externalReference: { id: reference, source: EHL_REFERENCE }
          }
        });

        await importHotelBooking({
          variables: {
            options: {
              provider: 'JACTRAVEL',
              product: 'HOTELS',
              bookingCode: jacTravelRef,
              market: 'UK',
              language: 'EN',
              pnr: reference
            }
          }
        });
      }

      history.push({
        pathname: `/booking/${reference}/pax`,
        state: { isHotelOnly: true }
      });
    } catch (err) {
      await cancelBooking({
        variables: { bookingReference: reference }
      });
    }

    setIsCreatingBooking(false);
  };

  if (error) {
    throw new AgentPortalError(error, ACTION_CODES.REDIRECT_TO_CUSTOM_PAGE, {
      errorCTARedirect: '/'
    });
  }

  return (
    <HotelOnlyContainer>
      <StyledHeader>
        <h1>
          <FormattedMessage id="homepage.hotelBooking" />
        </h1>
      </StyledHeader>
      <StyledFormContainer>
        <StyledInputWrapper>
          <label htmlFor="jacTravelRef">
            <StyledLabel>
              <FormattedMessage id="extras.hotel.modal.jacTravelRef" />
            </StyledLabel>
            <input
              type="text"
              data-cy="jacTravelRef"
              data-testid="jacTravelRef"
              {...register('jacTravelRef', {
                required: true,
                ...(!hotelInfo?.isDotW && {
                  pattern: jacTravelReferenceRegex
                })
              })}
            />
          </label>
          <SubmitButton
            type="submit"
            onClick={onSubmitJacTravelRef}
            disabled={isHotelFormSubmitDisabled}
            loading={isGetHotelInfoLoading}
            data-cy="jacTravelRefSubmitButton"
            data-testid="jacTravelRefSubmitButton"
          >
            <FormattedMessage id="paxForm.enter" />
          </SubmitButton>
        </StyledInputWrapper>
        <StyledInputWrapper>
          <label htmlFor="hotelId">
            <StyledLabel>
              <FormattedMessage id="extras.hotel.modal.hotelId" />
            </StyledLabel>
            <input
              type="text"
              data-cy="hotelId"
              data-testid="hotelId"
              disabled={!isHotelBeenFound || hotelInfo?.isDotW}
              {...register('hotelId', !hotelInfo?.isDotW && { required: false, pattern: hotelIdRegex, maxLength: 6 })}
            />
          </label>
        </StyledInputWrapper>
        <StyledInputWrapper>
          <label htmlFor="eurostarPackageRef">
            <StyledLabel>
              <FormattedMessage id="extras.hotel.modal.eurostarPackageRef" />
            </StyledLabel>
            <input
              type="text"
              data-cy="eurostarPackageRef"
              data-testid="eurostarPackageRef"
              disabled={!isHotelBeenFound || hotelInfo?.isDotW}
              {...register('eurostarPackageRef', { required: false })}
            />
          </label>
        </StyledInputWrapper>
      </StyledFormContainer>
      {errorKey && (
        <StyledErrorMessage data-testid="errors" data-cy="errors">
          <FormattedMessage id={errorKey} />
        </StyledErrorMessage>
      )}
      {!!hotelInfo && (
        <HotelDetailsContainer>
          <Row>
            <Column span={4}>
              <VerticalLine>
                <strong>
                  <FormattedMessage id="extras.hotel.modal.passengerName" />
                </strong>
                <div>{hotelInfo.leadGuestLastName}</div>
              </VerticalLine>
            </Column>
            <Column span={4}>
              <VerticalLine>
                <strong>
                  <FormattedMessage id="extras.hotel.modal.checkInDate" />
                </strong>
                <div>{hotelInfo.arrivalDate}</div>
              </VerticalLine>
            </Column>
            <Column span={4}>
              <VerticalLine>
                <strong>
                  <FormattedMessage id="extras.hotel.modal.hotelName" />
                </strong>
                <div>{hotelInfo.hotelName}</div>
              </VerticalLine>
            </Column>
            <Column span={4}>
              <strong>
                <FormattedMessage id="extras.hotel.modal.numberOfNights" />
              </strong>
              <div>{hotelInfo.duration}</div>
            </Column>
          </Row>
          <StyledHr />
          <Row>
            <Column span={4}>
              <VerticalLine>
                <strong>
                  <FormattedMessage id="extras.hotel.modal.guestCount" />
                </strong>
                <div>{hotelInfo.numberOfGuests}</div>
              </VerticalLine>
            </Column>
            <Column span={4}>
              <VerticalLine>
                <strong>
                  <FormattedMessage id="extras.hotel.modal.roomCount" />
                </strong>
                <div>{hotelInfo.numberOfRooms}</div>
              </VerticalLine>
            </Column>
            <Column span={4}>
              <VerticalLine>
                <strong>
                  <FormattedMessage id="extras.hotel.modal.hotelAddress" />
                </strong>
                <div>{hotelInfo.address}</div>
              </VerticalLine>
            </Column>
          </Row>
          <StyledHr />
          <Row>
            <StyledPrice>
              <strong>
                <FormattedMessage id="extras.hotel.modal.price" />:{' '}
              </strong>
              <FormattedCurrency currency={hotelInfo.currency} amount={hotelInfo.isDotW ? hotelInfo.priceBeforeMargin : hotelInfo.price} />
            </StyledPrice>
          </Row>
          <Row>
            <StyledPrice>
              <strong>
                <FormattedMessage id="extras.hotel.margin" />:{' '}
              </strong>
              <FormattedCurrency currency={hotelInfo.currency} amount={hotelInfo.margin} />
            </StyledPrice>
          </Row>
          <Row>
            <StyledPrice>
              <strong>
                <FormattedMessage id="extras.total" />:{' '}
              </strong>
              <FormattedCurrency
                currency={hotelInfo.currency}
                amount={sum([hotelInfo.isDotW ? hotelInfo.priceBeforeMargin : hotelInfo.price, hotelInfo.margin])}
              />
            </StyledPrice>
          </Row>
        </HotelDetailsContainer>
      )}
      <ConfirmButton
        data-testid="create-hotel-only-booking"
        data-cy="create-hotel-only-booking"
        loading={isCreatingBooking}
        disabled={!isCreateBookingButtonEnabled}
        onClick={createHotelBooking}
      >
        <FormattedMessage id="extrasOnlyBookings.submitFree" />
        <ChevronRight />
      </ConfirmButton>
    </HotelOnlyContainer>
  );
};

export default HotelOnly;
